import { type Optional, stringUtils } from '@festina-life/core'
import { useThemeContext } from '@festina-life/ui/theme'
import { useMemo } from 'react'

import type { MarkdownAssetsMap } from './types'

/**
 * Hook that processes markdown content to replace relative image paths with proper Vite imports
 *
 * This hook solves the problem of local image references in markdown content not working
 * when imported as a string in Vite. It processes the markdown content to replace relative
 * image paths with proper Vite-processed URLs.
 *
 * ## Usage
 *
 * ```tsx
 * import { useMarkdownMediaProcessor } from './markdownviewer'
 * import myMarkdown from './myMarkdown.md'
 * import { myImageMap } from './myImageMap'
 *
 * const MyComponent = () => {
 *   const processedMarkdown = useMarkdownMediaProcessor(myMarkdown, myImageMap)
 *   return <MarkdownViewer>{processedMarkdown}</MarkdownViewer>
 * }
 * ```
 *
 * ## How it works
 *
 * 1. **Image Imports**: Images are imported in a separate module using Vite's static asset handling
 * 2. **Path Mapping**: The `imageMap` parameter connects relative paths in markdown to the imported image URLs
 * 3. **Content Processing**: The hook uses regex to find and replace image references in markdown content
 * 4. **Caching**: The processed content is memoized to avoid unnecessary re-processing
 *
 * ## Adding new images
 *
 * 1. Import the image in your image map module:
 *    ```tsx
 *    import newImage from './path/to/image.png'
 *    ```
 *
 * 2. Add it to your image map:
 *    ```tsx
 *    export const myImageMap: Record<string, string> = {
 *      './path/to/image.png': newImage,
 *    }
 *    ```
 *
 * 3. Use the relative path in your markdown:
 *    ```markdown
 *    ![Description](./path/to/image.png)
 *    ```
 *
 * @param markdownContent - The markdown content to process
 * @param  mediaMap - Map of relative paths to imported image URLs
 * @returns The processed markdown content with image URLs replaced
 */
export const useMarkdownMediaProcessor = (
  markdownContent: Optional<string>,
  mediaMap: MarkdownAssetsMap,
): string => {
  const { isDarkMode } = useThemeContext()

  return useMemo(() => {
    if (!markdownContent) {
      return ''
    }

    let processedContent = markdownContent

    // Replace all image references with their proper URLs
    Object.entries(mediaMap).forEach(([relativePath, replacement]) => {
      // For example, './images/foo.png' becomes '\\./images/foo\\.png'
      const escapedRelativePath = stringUtils.escapeRegexp(relativePath)

      // Match both ![alt](path) and ![alt text](path) patterns
      // This regex matches markdown image syntax: ![alt text](path)
      // This regex matches markdown image syntax: ![alt text](path)
      // Explanation:
      // - `!\\[` matches the literal characters `![`
      // - `([^\\]]*)` captures any characters except `]` (the alt text) into group 1
      // - `\\]` matches the closing bracket `]`
      // - `\\(` matches the opening parenthesis `(`
      // - `${escapedRelativePath}` matches the (now safely escaped) image path literally
      // - `\\)` matches the closing parenthesis `)`
      // The 'g' flag means global search (replace all matches)
      const regex = new RegExp(
        `!\\[([^\\]]*)\\]\\(${escapedRelativePath}\\)`,
        'g',
      )

      const mode = isDarkMode ? 'dark' : 'light'
      const imageUrl =
        typeof replacement === 'string' ? replacement : replacement[mode]

      processedContent = processedContent.replace(regex, `![$1](${imageUrl})`)
    })

    return processedContent
  }, [markdownContent, mediaMap, isDarkMode])
}
